import type { CSSProperties } from 'react';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { Icon, type IconTypes } from '../Icon/Icon';

type InfoBoxSeverity = 'info' | 'success' | 'warn' | 'error';

const severityIcon: Record<InfoBoxSeverity, IconTypes> = {
    info: 'info',
    success: 'check_circle',
    warn: 'warning',
    error: 'error',
};

interface InfoBoxProps {
    severity?: InfoBoxSeverity;
    children?: ReactNode;
    items?: string[];
    className?: string;
    style?: CSSProperties;
    /**
     * Icon size. Defaults to 'small'
     */
    iconSize?: 'small' | 'normal' | 'large';
    /**
     * Overrides the icon shown for the given severity.
     */
    icon?: IconTypes;
    /**
     * Optional bold heading rendered above the content.
     */
    title?: ReactNode;
}

export function InfoBox(props: InfoBoxProps): ReactNode {
    const {
        children,
        items,
        severity = 'info',
        iconSize = 'small',
        className,
        style,
        icon,
        title,
    } = props;
    const boxClassName = clsx('info-box', severity, className);

    return (
        <div className={boxClassName} style={style}>
            <Icon
                className={severity}
                name={icon ?? severityIcon[severity]}
                fontSize={iconSize}
            />
            <div className="flex flex-column gap-2">
                {title && <strong>{title}</strong>}
                {items
                    ? items.map((item, index) => (
                          <span key={index}>
                              {items.length > 1 ? `${index + 1}. ` : ''}
                              {item}
                          </span>
                      ))
                    : children}
            </div>
        </div>
    );
}
